/**
 * Resolve an injected EIP-1193 provider without going through Brave window.ethereum Proxy wrapper.
 *
 * Brave installs a Proxy that recursively wraps every property access on
 * window.ethereum / web3. Native Map/Set methods then throw Map receiver errors.
 *
 * EIP-6963 announces the raw provider objects, so prefer those.
 */

export type Eip6963ProviderDetail = {
  info: {
    uuid: string
    name: string
    icon: string
    rdns: string
  }
  provider: {
    request?: (args: { method: string; params?: unknown[] }) => Promise<unknown>
    send?: (method: string | { method: string; params?: unknown[] }, params?: unknown[]) => Promise<unknown>
    enable?: () => Promise<string[]>
    on?: (event: string, listener: (...args: unknown[]) => void) => void
    removeListener?: (event: string, listener: (...args: unknown[]) => void) => void
    chainId?: string
    isMetaMask?: boolean
    isBraveWallet?: boolean
    providers?: unknown[]
    autoRefreshOnNetworkChange?: boolean
  }
}

const announced: Eip6963ProviderDetail[] = []
let listening = false

function isBraveDetail(detail: Eip6963ProviderDetail) {
  const rdns = detail.info?.rdns || ''
  const name = detail.info?.name || ''
  return rdns === 'com.brave.wallet' || /brave/i.test(name) || Boolean(detail.provider?.isBraveWallet)
}

function isMetaMaskDetail(detail: Eip6963ProviderDetail) {
  const rdns = detail.info?.rdns || ''
  const name = detail.info?.name || ''
  return (
    rdns === 'io.metamask' ||
    rdns === 'io.metamask.flask' ||
    /metamask/i.test(name) ||
    Boolean(detail.provider?.isMetaMask && !detail.provider?.isBraveWallet)
  )
}

function ensureEip6963Listener() {
  if (typeof window === 'undefined' || listening) return
  listening = true
  window.addEventListener('eip6963:announceProvider', ((event: Event) => {
    const detail = (event as CustomEvent<Eip6963ProviderDetail>).detail
    if (!detail?.provider || !detail?.info?.uuid) return
    if (announced.some((a) => a.info.uuid === detail.info.uuid)) return
    announced.push(detail)
  }) as EventListener)
}

export function discoverEip6963Providers(): Eip6963ProviderDetail[] {
  if (typeof window === 'undefined') return []
  ensureEip6963Listener()
  try {
    window.dispatchEvent(new Event('eip6963:requestProvider'))
  } catch {
    /* ignore */
  }
  return [...announced]
}

type WindowEthereum = Eip6963ProviderDetail['provider']

export function getInjectedEthereum(): WindowEthereum | null {
  if (typeof window === 'undefined') return null

  const providers = discoverEip6963Providers()

  const metamask = providers.find(isMetaMaskDetail)
  if (metamask?.provider) return metamask.provider

  const nonBrave = providers.find((d) => !isBraveDetail(d))
  if (nonBrave?.provider) return nonBrave.provider

  const eth = (window as unknown as { ethereum?: WindowEthereum }).ethereum
  if (!eth) {
    const brave = providers.find(isBraveDetail)
    return brave?.provider || null
  }

  try {
    if (Array.isArray(eth.providers) && eth.providers.length > 0) {
      const mm = (eth.providers as WindowEthereum[]).find((p) =>
        Boolean(p?.isMetaMask && !p?.isBraveWallet)
      )
      if (mm) return mm
    }
  } catch {
    /* Brave proxy may throw while reading providers */
  }

  const brave = providers.find(isBraveDetail)
  if (brave?.provider) return brave.provider

  return eth
}

export function hasInjectedMetaMask(): boolean {
  if (typeof window === 'undefined') return false
  const providers = discoverEip6963Providers()
  if (providers.some(isMetaMaskDetail)) return true

  try {
    const eth = (window as unknown as { ethereum?: WindowEthereum }).ethereum
    if (!eth) return false
    if (eth.isMetaMask && !eth.isBraveWallet) return true
    if (Array.isArray(eth.providers)) {
      return (eth.providers as WindowEthereum[]).some((p) =>
        Boolean(p?.isMetaMask && !p?.isBraveWallet)
      )
    }
  } catch {
    /* ignore */
  }
  return false
}

export function hasAnyInjectedWallet(): boolean {
  if (typeof window === 'undefined') return false
  if (discoverEip6963Providers().length > 0) return true
  try {
    return Boolean((window as unknown as { ethereum?: unknown }).ethereum)
  } catch {
    return false
  }
}

/** Point window.ethereum at the resolved raw provider for legacy web3-react code. */
export function ensureResolvedInjectedProvider(): WindowEthereum | null {
  const resolved = getInjectedEthereum()
  if (!resolved || typeof window === 'undefined') return resolved

  try {
    const current = (window as unknown as { ethereum?: WindowEthereum }).ethereum
    if (current !== resolved) {
      ;(window as unknown as { ethereum?: WindowEthereum }).ethereum = resolved
    }
  } catch {
    /* assignment blocked — caller should use getInjectedEthereum() directly */
  }

  return resolved
}

export function installBraveProxyErrorGuard(): () => void {
  if (typeof window === 'undefined') return () => {}

  const isMapReceiverError = (value: unknown) =>
    /Map\.prototype\.(has|get|set|delete) called on incompatible receiver/i.test(
      String((value as { message?: string })?.message || value || '')
    )

  const onError = (event: ErrorEvent) => {
    if (isMapReceiverError(event.error) || isMapReceiverError(event.message)) {
      event.preventDefault()
      event.stopImmediatePropagation?.()
    }
  }

  const onRejection = (event: PromiseRejectionEvent) => {
    if (isMapReceiverError(event.reason)) event.preventDefault()
  }

  window.addEventListener('error', onError, true)
  window.addEventListener('unhandledrejection', onRejection)
  return () => {
    window.removeEventListener('error', onError, true)
    window.removeEventListener('unhandledrejection', onRejection)
  }
}